<script setup lang="ts">
import type { SectionStatuses } from '~/store-v2/financings/useFinancing'
import type { StageFormalization, TagStep } from '~/ui-blocks/Formalization/Formalization.types'
import type { AcquisitionKit, HardwareCombosModel } from '~/utils/installation/Installation'
import { SolAlert, SolButton, SolDivider, useToast } from '@solfacil/girassol'
import IconLock from '~icons/material-symbols/lock'
import CancelEquipmentEditModal from '~/components/customer/modals/CancelEquipmentEditModal.vue'
import SegmentedToggle from '~/components/input/SegmentedToggle.vue'
import { useEquipmentEditPermissions } from '~/composables/equipment/useEquipmentEditPermissions'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import { useTerminalStatusLock } from '~/composables/financing/useTerminalStatusLock'
import { useDateProps } from '~/composables/proposal/useDateProps'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'
import useAuth from '~/store-v2/auth/useAuth'
import useCustomerBiometry from '~/store-v2/contract-formalization/useCustomerBiometry'
import useFinancing from '~/store-v2/financings/useFinancing'
import { FinancingStatus } from '~/store-v2/financings/useFinancings'
import useSaveHardware from '~/store-v2/installation/useSaveHardware'
import useProjectContractSigned from '~/store-v2/projects/useProjectContractSigned'
import { useEquipmentEditStore } from '~/store/equipmentEdit'
import { Formalization } from '~/types/enum'
import StatusDates from '~/ui-blocks/Formalization/StatusDates.vue'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'
import { ACQUISITION_KIT } from './equipment.constants'
import ManualKitForm from './ManualKitForm.vue'
import { getOrderFieldError, parseSaveError, resolveStoreOrderSaveError } from './storeOrderErrors'
import { getStoreOrderReasonAlertDescriptionKey, isStoreOrderFinancingReason } from './storeOrderReason'
import StoreOrderSelect from './StoreOrderSelect.vue'
import { useAcquisitionKitOptions } from './useAcquisitionKitOptions'

const props = defineProps<{
  position: number
  stage: StageFormalization
  stageReproved: string
  statusProject: SectionStatuses
  formData?: HardwareCombosModel
  refetch: () => Promise<void>
  isHardwareFormLoading?: boolean
  isHardwareFormError?: boolean
}>()

const { t, d } = useI18n()
const route = useRoute<any>()
const toast = useToast()
const projectId = computed(() => String(route.params.id))
const { data: financing, refetch: refetchFinancing } = useFinancing(projectId)
const { isTerminalLocked, isTerminalReadOnly, terminalTag } = useTerminalStatusLock(financing, 'equipment', t)
const { hasPendingTAF } = usePendingUpdateEquipment(projectId)

const hasSelectedHardware = ref(false)
const hasSelectedSystemInfo = ref(false)
const blocked = ref(true)

// Lock caused by another reproved stage; ignored in terminal financing so started steps can be viewed.
const isStageReprovedBlocked = computed(() => blocked.value && !isTerminalReadOnly.value)

const equipmentEditStore = useEquipmentEditStore()
const isEditingEquipment = computed(() => equipmentEditStore.isProjectBeingEdited(projectId.value))

const { data: auth } = useAuth()
const { data: isContractSigned } = useProjectContractSigned(projectId)
const isContractStepAvailable = computed(() => !!financing.value?.openable_steps?.contract)
const { data: customerBiometry } = useCustomerBiometry(projectId, isContractStepAvailable)

const {
  isEditButtonDisabled,
} = useEquipmentEditPermissions({
  financing,
  customerBiometry,
  isContractSigned,
  auth,
  isEditingEquipment,
  hasPendingTAF,
})

const tag = computed(() => {
  if (isEditingEquipment.value) {
    const inProgressMapping = STATUSES_MAPPING[GeneralStatuses.IN_PROGRESS]

    return {
      id: GeneralStatuses.IN_PROGRESS,
      text: t(inProgressMapping?.textKey || ''),
      variant: 'fill',
      size: 'medium',
      type: inProgressMapping?.color || '',
    } as TagStep
  }

  if (props.statusProject?.equipment?.status) {
    const statusMapping = STATUSES_MAPPING[props.statusProject.equipment.status]

    return {
      id: props.statusProject?.equipment?.status || '',
      text: t(statusMapping?.textKey || ''),
      variant: 'fill',
      size: 'medium',
      type: statusMapping?.color || '',
    } as TagStep
  }

  return null
})

watch(
  tag,
  () => {
    refetchFinancing()
  },
  { immediate: true },
)

onMounted(async () => {
  checkStageReproved()
  hasSelectedHardware.value = (props.formData?.modules?.length ?? 0) > 0 && ((props.formData?.inverters?.length ?? 0) > 0 || (props.formData?.microinverters?.length ?? 0) > 0)
  hasSelectedSystemInfo.value = Boolean(props.formData?.distributor && props.formData.phase)
})

function checkStageReproved() {
  if (props.stageReproved === '')
    blocked.value = false
  else
    blocked.value = props.stageReproved !== Formalization.EQUIPMENT
}

function openCollapse() {
  const isEquipmentStage = props.stage === Formalization.EQUIPMENT
  const openHardware = financing.value?.openable_steps?.hardware
  if (!isEquipmentStage || isStageReprovedBlocked.value || !openHardware)
    return false

  const status = financing.value?.section_statuses?.equipment?.status
  const hasIncompleteSelection = !(hasSelectedHardware.value && hasSelectedSystemInfo.value)
  const expandStatuses: GeneralStatuses[] = [
    GeneralStatuses.APPROVED,
    GeneralStatuses.IN_PROGRESS,
    GeneralStatuses.TO_REVIEW,
    GeneralStatuses.UNDER_ANALYSIS,
    GeneralStatuses.REPROVED,
  ]

  return hasIncompleteSelection || (!!status && expandStatuses.includes(status as GeneralStatuses))
}

const submittedAt = computed(() => financing.value?.section_statuses?.equipment?.submitted_at)
const completedAt = computed(() => financing.value?.section_statuses?.equipment?.completed_at)
const reprovedAt = computed(() => financing.value?.section_statuses?.equipment?.reproved_at)

const dateProps = useDateProps('hardware', {
  completedAt,
  reprovedAt,
  submittedAt,
  blocked: isStageReprovedBlocked,
  financing,
})

const isApprovedPaymentModel = computed(() => financing.value?.section_statuses?.payment_model?.status === 'approved')

const isApprovedOrReprovedOrExpiredOrCanceledStatus = computed(() => {
  return [
    FinancingStatus.approved,
    FinancingStatus.reproved,
    FinancingStatus.expired,
    FinancingStatus.canceled,
  ].includes(financing.value?.status as FinancingStatus)
})

const isApprovedStepStatus = computed(() => {
  return financing.value?.section_statuses?.equipment?.status === 'approved'
})

const hasShownStatus = computed(() => isApprovedPaymentModel.value || isApprovedOrReprovedOrExpiredOrCanceledStatus.value || isApprovedStepStatus.value)
const showDateProps = computed(() => submittedAt.value || completedAt.value || reprovedAt.value || !hasShownStatus.value)

const isCombo = computed(() => Boolean(props.formData?.is_combo))

// Single toggle driven by the backend (aquisition_kit_available from GET /hardware/form).
const { options: acquisitionOptions, firstEnabled } = useAcquisitionKitOptions(
  () => props.formData?.aquisition_kit_available,
  t,
)

function isRenderedAcquisitionKit(value: AcquisitionKit | null | undefined): value is AcquisitionKit {
  return value !== null
    && value !== undefined
    && acquisitionOptions.value.some(option => option.value === value)
}

function getAcquisitionKitFromForm(): AcquisitionKit | null {
  const savedAcquisitionKit = props.formData?.selected_aquisition_kit
  return isRenderedAcquisitionKit(savedAcquisitionKit)
    ? savedAcquisitionKit
    : firstEnabled.value
}

const acquisitionKit = ref<AcquisitionKit | null>(getAcquisitionKitFromForm())
watch(
  [acquisitionOptions, () => props.formData?.selected_aquisition_kit],
  () => {
    // Preserve a valid local choice during background refetches.
    if (!isRenderedAcquisitionKit(acquisitionKit.value))
      acquisitionKit.value = getAcquisitionKitFromForm()
  },
  { immediate: true },
)

const orderId = ref<string>(props.formData?.order_store?.order_id ?? '')
const orderError = ref('')
const orderExpired = ref(false)
const orderInvalidAlert = ref<{ title: string, text: string } | null>(null)
const showCancelModal = ref(false)

watch(() => props.formData?.errors, (errors) => {
  const fieldError = getOrderFieldError(errors)
  if (fieldError)
    orderError.value = fieldError
}, { immediate: true })

const isStoreOrderDisabled = computed(() => {
  // Terminal financing (expired / reproved / canceled) wins over the persisted edit mode.
  if (isTerminalReadOnly.value)
    return true
  if (isEditingEquipment.value)
    return false
  const status = financing.value?.section_statuses?.equipment?.status
  return blocked.value || status === GeneralStatuses.APPROVED || status === 'under_analysis'
})

const isStoreOrderSaved = computed(() => {
  const status = financing.value?.section_statuses?.equipment?.status
  return status === GeneralStatuses.APPROVED || status === 'under_analysis'
})

const hasPaidTransfer = computed(() =>
  financing.value?.section_statuses?.payment_model?.status === GeneralStatuses.APPROVED,
)

const storeOrderEditDisabled = computed(() => isEditButtonDisabled.value)

// Global toggle disabled state: status/edit lock + lock when payment is approved.
// The per-option disabled state (backend `false`) comes from `acquisitionOptions[].disabled`.
const isToggleDisabled = computed(() => isStoreOrderDisabled.value || hasPaidTransfer.value)

const showStoreOrder = computed(() => acquisitionKit.value === ACQUISITION_KIT.MIRROR)
const showManualKit = computed(() =>
  acquisitionKit.value === ACQUISITION_KIT.SELECTED
  || acquisitionKit.value === ACQUISITION_KIT.CONTAINER
  || acquisitionKit.value === ACQUISITION_KIT.OWN_EQUIPMENT,
)

const showDeadlineAlert = computed(() =>
  financing.value?.section_statuses?.equipment?.status === GeneralStatuses.IN_PROGRESS
  && !!props.formData?.expiration_date,
)

const storeOrderFinancingReason = computed(() => financing.value?.reason ?? null)

const storeOrderReasonAlert = computed(() => {
  const reason = storeOrderFinancingReason.value
  if (!isStoreOrderFinancingReason(reason))
    return null

  return {
    title: t('customer.equipment_step.store_order_reason_alert.title'),
    description: t(getStoreOrderReasonAlertDescriptionKey(reason)),
  }
})

const savedStoreOrderId = computed(() => props.formData?.order_store?.order_id ?? '')

const isSameStoreOrderAsBackend = computed(() =>
  Boolean(orderId.value)
  && Boolean(savedStoreOrderId.value)
  && orderId.value === savedStoreOrderId.value,
)

const isSaveStoreOrderBlockedByReason = computed(() =>
  isStoreOrderFinancingReason(storeOrderFinancingReason.value)
  && isSameStoreOrderAsBackend.value,
)

const { mutateAsync: mutateSaveHardware, isPending: isSavingStoreOrder } = useSaveHardware(projectId)

function clearOrderErrors() {
  orderError.value = ''
  orderExpired.value = false
  orderInvalidAlert.value = null
}

function restoreSavedAcquisitionState() {
  acquisitionKit.value = getAcquisitionKitFromForm()
  orderId.value = props.formData?.order_store?.order_id ?? ''
  clearOrderErrors()
}

watch(
  [isTerminalReadOnly, isEditingEquipment],
  ([isReadOnly, isEditing]) => {
    if (!isReadOnly)
      return

    showCancelModal.value = false
    if (isEditing)
      equipmentEditStore.stopEditingEquipment()

    restoreSavedAcquisitionState()
  },
  { immediate: true },
)

function handleEditStoreOrder() {
  if (isTerminalReadOnly.value)
    return

  equipmentEditStore.startEditingEquipment(projectId.value)
}

function handleCancelStoreOrderClick() {
  if (isTerminalReadOnly.value)
    return

  showCancelModal.value = true
}

function handleCancelEdit() {
  if (isTerminalReadOnly.value)
    return

  showCancelModal.value = false
  equipmentEditStore.stopEditingEquipment()
  restoreSavedAcquisitionState()
}

async function saveStoreOrder() {
  if (isTerminalReadOnly.value)
    return

  clearOrderErrors()

  if (!orderId.value) {
    orderError.value = t('form.required')
    return
  }

  try {
    await mutateSaveHardware({
      is_combo: isCombo.value,
      project_id: projectId.value,
      order_id: orderId.value,
      selected_aquisition_kit: ACQUISITION_KIT.MIRROR,
    })
    toast.createSuccessToast(t('customer.equipment_success_save'))
    equipmentEditStore.stopEditingEquipment()
    await props.refetch()
  }
  catch (error: any) {
    const result = resolveStoreOrderSaveError(error)

    switch (result.kind) {
      case 'store-order-status':
        if (result.markExpired)
          orderExpired.value = true
        if (result.showFieldError)
          orderError.value = t(result.messageKey)
        toast.createErrorToast(t(result.messageKey))
        break
      case 'select':
        orderError.value = t(result.messageKey)
        toast.createErrorToast(t(result.messageKey))
        break
      case 'alert':
        orderInvalidAlert.value = { title: t(result.titleKey), text: result.message }
        break
      case 'field-toast':
        orderError.value = t(result.messageKey)
        toast.createErrorToast(t(result.messageKey))
        break
      default: {
        const { message } = parseSaveError(error)
        toast.createErrorToast(message || t('customer.equipment_error_save'))
        break
      }
    }
  }
}
</script>

<template>
  <CustomerElementAccordion
    :id="`accordion_${Formalization.EQUIPMENT}`"
    :title="t('customer.project_data_title')"
    :position="String(position)"
    :tag="terminalTag ?? (isStageReprovedBlocked || isTerminalLocked || !financing?.openable_steps?.hardware ? undefined : tag)"
    :blocked="isStageReprovedBlocked || isTerminalLocked || !financing?.openable_steps?.hardware"
    :open-collapse="openCollapse() && !isTerminalLocked"
    :has-shown-status="hasShownStatus"
    v-bind="showDateProps ? dateProps : null"
    :has-pending-update-equipment="hasPendingTAF"
    class="-mt-6 pb-6"
  >
    <template #icon>
      <SolTag v-if="hasPendingTAF" id="pending-update-equipment-tag" size="small">
        {{ t('customer.equipment_update.tag.pending') }}
      </SolTag>
      <IconLock v-if="isStageReprovedBlocked || isTerminalLocked || !financing?.openable_steps?.hardware" />
    </template>

    <div>
      <p class="mt-2">
        {{ t('customer.equipment_step.subtitle') }}
      </p>
    </div>

    <SolAlert
      v-if="storeOrderReasonAlert"
      id="store-order-reason-alert"
      class="mt-4"
      feedback="warning"
    >
      <p class="font-bold">
        {{ storeOrderReasonAlert.title }}
      </p>
      <p class="mt-1">
        {{ storeOrderReasonAlert.description }}
      </p>
    </SolAlert>

    <div
      v-if="props.isHardwareFormLoading"
      class="equipment-skeleton"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span class="sr-only">{{ t('customer.equipment_step.loading') }}</span>
      <div class="skeleton-title skeleton-shimmer" />
      <div class="skeleton-field">
        <div class="skeleton-label skeleton-shimmer" />
        <div class="skeleton-toggle skeleton-shimmer" />
      </div>
      <div class="skeleton-form skeleton-shimmer" />
    </div>

    <SolAlert
      v-if="!props.isHardwareFormLoading && showDeadlineAlert && props.formData"
      id="equipment-deadline-alert"
      class="mt-4"
      feedback="warning"
    >
      <p class="font-bold">
        {{ t('customer.equipment_step.deadline_alert.title') }}
      </p>
      <p class="mt-1">
        {{ t('customer.equipment_step.deadline_alert.description', { date: d(props.formData.expiration_date as string, 'short') }) }}
      </p>
    </SolAlert>

    <div v-if="!props.isHardwareFormLoading && props.formData" class="mt-6 flex flex-col gap-6">
      <span class="font-highlight text-neutral-low-dark text-[20px]">{{ t('customer.equipment_step.section_title') }}</span>

      <div v-if="acquisitionOptions.length" class="flex flex-col gap-2">
        <span class="fonts-body-medium-bold text-neutral-low-dark">{{ t('customer.equipment_step.acquisition.label') }}</span>
        <div class="flex flex-col md:system:flex-row md:system:items-stretch gap-4">
          <SegmentedToggle
            v-model="acquisitionKit"
            :options="acquisitionOptions"
            :disabled="isToggleDisabled"
            fit
            class="self-start max-w-full"
            aria-label="Forma de aquisição do kit"
          />
        </div>
      </div>

      <template v-if="showStoreOrder">
        <SolAlert
          v-if="orderInvalidAlert"
          id="store-order-invalid-alert"
          feedback="warning"
          :title="orderInvalidAlert.title"
        >
          {{ orderInvalidAlert.text }}
        </SolAlert>

        <StoreOrderSelect
          :project-id="projectId"
          :order-id="orderId"
          :order-value="props.formData?.order_store?.equipment_value"
          :disabled="isStoreOrderDisabled"
          :read-only="isTerminalReadOnly"
          :error="orderError"
          :expired="orderExpired"
          @update:order-id="(value) => { orderId = value; clearOrderErrors() }"
        />

        <SolDivider thickness="x-small" orientation="horizontal" />
        <div :class="`flex ${showDateProps ? 'justify-between' : 'justify-end'} w-full gap-3 sm:system:items-center flex-col sm:system:flex-row`">
          <StatusDates
            v-if="showDateProps"
            :submitted-at="submittedAt"
            :completed-at="completedAt"
            :reproved-at="reprovedAt"
            :order-changed-at="props.formData?.order_store?.order_changed_at"
          />
          <div class="flex flex-col sm:system:flex-row gap-3 w-full sm:system:w-auto">
            <SolButton
              v-if="isStoreOrderSaved && !isEditingEquipment"
              id="edit_store_order"
              size="large"
              variant="secondary"
              class="w-full md:system:w-auto"
              :disabled="storeOrderEditDisabled"
              @click="handleEditStoreOrder"
            >
              {{ t('app.edit_equipment') }}
            </SolButton>

            <SolButton
              v-if="isEditingEquipment"
              id="cancel_edit_store_order"
              size="large"
              variant="secondary"
              class="w-full md:system:w-auto"
              :disabled="isTerminalReadOnly"
              @click="handleCancelStoreOrderClick"
            >
              {{ t('app.cancel_equipment_edit') }}
            </SolButton>

            <SolButton
              v-if="!isStoreOrderSaved || isEditingEquipment"
              id="save_store_order"
              size="large"
              class="w-full md:system:w-auto"
              :loading="isSavingStoreOrder"
              :disabled="isStoreOrderDisabled || !orderId || isSaveStoreOrderBlockedByReason"
              @click="saveStoreOrder"
            >
              {{ t('customer.equipment_step.actions.save_continue') }}
            </SolButton>
          </div>
        </div>
      </template>

      <ManualKitForm
        v-else-if="showManualKit"
        :hardware-information="props.formData"
        :refetch-form="props.refetch"
        :show-date-props="Boolean(showDateProps)"
        :submitted-at="submittedAt"
        :completed-at="completedAt"
        :reproved-at="reprovedAt"
        :show-structure="true"
        :selected-acquisition-kit="acquisitionKit ?? undefined"
        @cancel-edit="handleCancelEdit"
      />
    </div>

    <div v-else-if="!props.isHardwareFormLoading && props.isHardwareFormError" class="my-4 card-container flex flex-col justify-between w-full align-middle py-lg text-center items-center">
      <img src="/images/not-found.png" class="not-found">
      <h1 class="fonts-heading-h1">
        Ops, algo deu errado
      </h1>
      <p class="fonts-subtitle-medium">
        Ocorreu algum problema e não conseguimos trazer os dados dos seus equipamentos.
      </p>
    </div>
  </CustomerElementAccordion>

  <CancelEquipmentEditModal
    v-if="showStoreOrder"
    :is-open="showCancelModal"
    @close="showCancelModal = false"
    @confirm="handleCancelEdit"
    @cancel="showCancelModal = false"
  />
</template>

<style lang="scss" scoped>
.equipment-skeleton {
  @apply mt-6 flex flex-col gap-6;

  .skeleton-title {
    @apply h-6 w-48 rounded-sm;
  }

  .skeleton-field {
    @apply flex flex-col gap-2;

    .skeleton-label {
      @apply h-5 w-36 rounded-sm;
    }

    .skeleton-toggle {
      @apply h-12 w-full max-w-[520px] rounded-md;
    }
  }

  .skeleton-form {
    @apply h-36 w-full rounded-md;
  }

  .skeleton-shimmer {
    @apply bg-brand-primary-light bg-opacity-semiopaque relative overflow-hidden;

    &::after {
      @apply content-[''] absolute inset-0 w-full h-full;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.4) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      animation: equipment-shimmer 1.5s infinite;
    }
  }
}

@keyframes equipment-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .equipment-skeleton .skeleton-shimmer::after {
    animation: none;
  }
}
</style>
